<template>
  <page-header title="Banners">
    <router-link
      to="/banner"
      class="btn-primary ml-auto h-10"
    >
      <PlusCircleIcon class="size-5 -ml-1 mr-2" />
      Add
    </router-link>
  </page-header>

  <div v-if="isEmpty && search === null" />

  <template v-else>
    <div class="flex justify-between mb-4">
      <input-search v-model.trim="search" class="max-w-lg grow" />

      <reload-button class="ml-4" @click="reloadGrid" />
    </div>

    <grid
      ref="gridRef"
      :data-handler="getBanners"
      :columns="columns"
      :row-class="rowClass"
      :per-page="15"
      @enable="onEnable"
      @disable="onDisable"
      @preview="onPreview"
      @delete="onDelete"
    />
  </template>

  <PreviewBanner v-model="isModalOpen" :item="currentBanner" />
</template>

<script setup>
import { ref, markRaw } from 'vue';
import PageHeader from '../components/PageHeader.vue';
import ReloadButton from '../components/grid/ReloadButton.vue';
import InputSearch from '../components/grid/InputSearch.vue';
import Grid from '../components/grid/Grid.vue';
import BannerListActions from '../components/BannerListActions.vue';
import { useFetch, watchDebounced } from '@vueuse/core';
import { useRouteQuery } from '@vueuse/router';
import { PlusCircleIcon } from '@heroicons/vue/24/outline';
import { useRouter } from 'vue-router';
import { apiDomain } from '../composables/useConstants';
import { Modal } from '../composables/useModal';
import { useToast } from 'vue-toastification';
import BannerBackground from '../components/BannerBackground.vue';
import BannerCondition from '../components/BannerCondition.vue';
import PreviewBanner from '../components/PreviewBanner.vue';
import { formatDate } from '../utils/formatters';

const gridRef = ref(null);
const isEmpty = ref(false);
const router = useRouter();
const toast = useToast();
const currentBanner = ref(null);
const isModalOpen = ref(false);


function rowClass(_row, index) {
  return (index % 2 ? 'bg-zinc-200/10' : '') + (!_row.isActive ? ' inactive-row' : '');
}

function reloadGrid(updateQuery = {}) {
  if (gridRef.value?.reload) {
    gridRef.value.reload(updateQuery);
  }
}

const search = useRouteQuery('search', null);

watchDebounced(
  search,
  () => {
    reloadGrid({ offset: 0 });
  },
  { debounce: 500, maxWait: 1000 }
);

function onPreview(row) {
  currentBanner.value = row;
  isModalOpen.value = true;
}

async function onEnable(row) {
  const confirm = await Modal.confirm({
    title: 'Enable Banner',
    body: `Enable the banner "${row.title}"?`,
    okButton: 'Enable',
    iconColor: 'primary'
  });

  if (!confirm) {
    return;
  }

  const { data } = await useFetch(`${apiDomain}/api/banner/enable/${row.id}`).get().json();

  if (data.value?.success) {
    toast.success('Banner enabled successfully');
    reloadGrid();
    return;
  }

  toast.error('Error enabling banner');
}

async function onDisable(row) {
  const confirm = await Modal.confirm({
    title: 'Disable Banner',
    body: `Disable the banner "${row.title}"?`,
    okButton: 'Disable',
    iconColor: 'primary'
  });

  if (!confirm) {
    return;
  }

  const { data } = await useFetch(`${apiDomain}/api/banner/disable/${row.id}`).get().json();

  if (data.value?.success) {
    toast.success('Banner disabled successfully');
    reloadGrid();
    return;
  }

  toast.error('Error disabling banner');
}

async function onDelete(row) {
  const confirm = await Modal.confirm({
    title: 'Delete Banner',
    body: `Are you sure you want to delete the banner "${row.title}"? This action cannot be undone.`,
    okButton: 'Delete',
    iconColor: 'danger'
  });

  if (!confirm) {
    return;
  }

  const { data } = await useFetch(`${apiDomain}/api/banner/${row.id}`).delete().json();

  if (data.value?.success) {
    toast.success('Banner deleted successfully');
    reloadGrid();
    return;
  }

  toast.error('Error deleting banner');
}

function getUrl({ limit, offset, sort, direction }) {
  const data = { limit, offset, order: sort, direction: direction?.toLowerCase(), search: search.value ?? '' };

  const searchParams = new URLSearchParams(data);

  return `${apiDomain}/api/banner/?${searchParams}`;
}

async function getBanners(args) {
  const { limit, offset, sort, direction } = args;
  const { data } = await useFetch(getUrl({ limit, offset, sort, direction })).get().json();

  isEmpty.value = false;

  return {
    total: data.value.total,
    rows: data.value.banners
  };
}

const columns = [
  {
    title: '#',
    prop: 'id',
    class: 'cursor-pointer text-xs font-mono',
    sortable: true,
    click: row => router.push(`/banner/${row.id}`)
  },
  {
    title: 'Order',
    prop: 'order',
    click: row => router.push(`/banner/${row.id}`)
  },
  {
    title: 'Background',
    component: markRaw(BannerBackground),
    click: row => router.push(`/banner/${row.id}`)
  },
  {
    title: 'Condition',
    component: markRaw(BannerCondition),
    click: row => router.push(`/banner/${row.id}`)
  },
  {
    title: 'Title',
    prop: 'title',
    class: 'max-w-48 truncate',
    click: row => router.push(`/banner/${row.id}`)
  },
  {
    title: 'Content',
    prop: 'content',
    class: 'max-w-56 truncate',
    click: row => router.push(`/banner/${row.id}`)
  },
  {
    title: 'Action',
    prop: 'actionButtonName',
    click: row => router.push(`/banner/${row.id}`)
  },
  {
    title: 'Created',
    prop: 'createdAt',
    value: row => formatDate(row.createdAt),
    sortable: true
  },
  {
    class: 'no-animation text-right max-w-max! z-10 relative',
    component: markRaw(BannerListActions)
  }
];
</script>
